import { collectorLaneViews } from "../collectors/lanes";
import { buildAutoLaunchView } from "../auto-launch/view";
import { buildCosmeticPanelView } from "../cosmetics/view";
import { buildSupplyCratesView } from "../crates/view";
import { buildDailyIgnitionView } from "../daily-ignition";
import { buildLuckySparkView, type LuckySparkRuntimeState } from "../events/lucky-spark";
import { buildOverdriveView } from "../events/overdrive";
import { buildReactorStormView, type ReactorStormRuntimeState } from "../events/reactor-storm";
import { lookupProfileProgress } from "../progression/levels";
import type { OrbitForgeSave } from "../save/types";
import { calculateUpgradeEffects } from "../upgrades/effects";
import { buildForgePanelView } from "../upgrades/view";
import type { CoreState, FeedbackEvent, GameSnapshot, ShellState } from "./types";

export interface RuntimeSnapshotInput {
  readonly save: OrbitForgeSave;
  readonly shells: readonly ShellState[];
  readonly cores: readonly CoreState[];
  readonly feedback: readonly FeedbackEvent[];
  readonly activePanel: string | null;
  readonly activeStorm: ReactorStormRuntimeState | null;
  readonly luckySpark: LuckySparkRuntimeState;
  readonly stormStopPending: boolean;
  readonly shellBoosting: boolean;
  readonly lastPayout: number;
  readonly manualCooldownSeconds: number;
  readonly loadedFrom: GameSnapshot["loadedFrom"];
  readonly nowUnixSeconds: number;
}

export function buildRuntimeSnapshot(input: RuntimeSnapshotInput): GameSnapshot {
  const progress = lookupProfileProgress(input.save.stats);
  const effects = calculateUpgradeEffects(input.save.upgrades, progress);
  const overdrive = buildOverdriveView(input.save, progress, Boolean(input.activeStorm), input.nowUnixSeconds);
  const storm = buildReactorStormView(input.save, progress, overdrive, input.activeStorm, input.stormStopPending);
  const overdriveKind = overdrive.active ? overdrive.activeKind : null;

  return {
    save: input.save,
    progress,
    effects,
    forge: buildForgePanelView(input.save, progress),
    cosmetics: buildCosmeticPanelView(input.save, progress),
    auto: buildAutoLaunchView(input.save, progress, Boolean(input.activeStorm)),
    crates: buildSupplyCratesView(input.save, progress, input.nowUnixSeconds),
    daily: buildDailyIgnitionView(input.save),
    events: {
      overdrive,
      luckySpark: buildLuckySparkView(input.save, input.luckySpark, progress),
      storm
    },
    arena: {
      shells: input.shells,
      cores: input.cores,
      lanes: collectorLaneViews(progress, effects, { overdriveKind }),
      shellBoosting: input.shellBoosting,
      activeProgressCores: input.cores.filter((core) => core.countsAgainstLaunchLimit).length
    },
    feedback: input.feedback,
    activePanel: input.activePanel,
    lastPayout: input.lastPayout,
    manualCooldownSeconds: input.manualCooldownSeconds,
    loadedFrom: input.loadedFrom
  };
}
